import React, { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import Chart from 'chart.js/auto';
import { getFinancials, getAopTargets, getGrowthDashboard } from '../../services/api';
import { useTheme } from '../../context/ThemeContext';
import { formatCurrency, formatCompactCurrency } from '../../components/shared/formatUtils';

const FONT_MONO = 'JetBrains Mono';
const FONT_MONO_STACK = 'JetBrains Mono, monospace';
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const MONTH_KEYS = ['2026-01', '2026-02', '2026-03', '2026-04', '2026-05', '2026-06', '2026-07', '2026-08', '2026-09', '2026-10', '2026-11', '2026-12'];
const DEAL_TYPES = ['BACKLOG', 'RECOMPETE', 'NB_OG', 'NEW_BIZ'];
const DEAL_TYPE_LABELS = {
  BACKLOG: 'Backlog',
  RECOMPETE: 'Recompete',
  NB_OG: 'Organic Growth',
  NEW_BIZ: 'New Business',
};
const DIVISIONS = ['MS', 'IS', 'Labs', 'Fuel'];
const DIVISION_LABELS = {
  MS: 'Mission Services',
  IS: 'Insight Solutions',
  Labs: 'B/CORE Labs',
  Fuel: 'FUEL',
};

const LAYER_COLORS = {
  BACKLOG:   { solid: 'rgba(96, 165, 250, 1)',    forecast: 'rgba(96, 165, 250, 0.18)',  border: '#60a5fa', forecastBorder: 'rgba(96, 165, 250, 0.35)' },
  RECOMPETE: { solid: 'rgba(52, 211, 153, 1)',    forecast: 'rgba(52, 211, 153, 0.18)',  border: '#34d399', forecastBorder: 'rgba(52, 211, 153, 0.35)' },
  NB_OG:     { solid: 'rgba(251, 191, 36, 1)',    forecast: 'rgba(251, 191, 36, 0.18)',  border: '#fbbf24', forecastBorder: 'rgba(251, 191, 36, 0.35)' },
  NEW_BIZ:   { solid: 'rgba(167, 139, 250, 1)',   forecast: 'rgba(167, 139, 250, 0.18)', border: '#a78bfa', forecastBorder: 'rgba(167, 139, 250, 0.35)' },
};

function chartColors(theme) {
  const isDark = theme !== 'light';
  return {
    textMuted: isDark ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.6)',
    textDim: isDark ? 'rgba(255, 255, 255, 0.35)' : 'rgba(0, 0, 0, 0.4)',
    gridLine: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.08)',
  };
}

function ForecastDashboard({ onNavigate, user }) {
  const { theme } = useTheme();

  const [financials, setFinancials] = useState(null);
  const [aopTargets, setAopTargets] = useState(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [selectedDivisions, setSelectedDivisions] = useState(null);
  const [contractRollup, setContractRollup] = useState('All');
  const [pwinOverrides, setPwinOverrides] = useState({});
  const [revenueTableOpen, setRevenueTableOpen] = useState(false);
  const [varianceTableOpen, setVarianceTableOpen] = useState(false);
  const [pwinSort, setPwinSort] = useState('name'); // 'name' or 'pwin'
  const [pwinSortDir, setPwinSortDir] = useState('asc'); // 'asc' or 'desc'
  const [growthData, setGrowthData] = useState(null);
  const [programDropdownOpen, setProgramDropdownOpen] = useState(false);
  const programDropdownRef = useRef(null);

  // Additional filters
  const [filterCustomer, setFilterCustomer] = useState('All');
  const [filterContractType, setFilterContractType] = useState(['All']);

  // Presentation mode
  const [presentationMode, setPresentationMode] = useState(false);

  // Customer dropdown state and ref
  const [customerDropdownOpen, setCustomerDropdownOpen] = useState(false);
  const customerDropdownRef = useRef(null);

  const hasLoadedRef = useRef(false);
  const revenueChartRef = useRef(null);
  const revenueChartInstance = useRef(null);
  const varianceChartRef = useRef(null);
  const varianceChartInstance = useRef(null);

  const groups = user?.groups || [];
  const canSeeAll = groups.includes('senior-leader') || groups.includes('investor') || groups.includes('admin');


  // Set initial division based on user access
  useEffect(() => {
    if (canSeeAll) {
      setSelectedDivisions(['All']);
    } else {
      setSelectedDivisions([DIVISIONS[0]]);
    }
  }, [canSeeAll]);

  // Determine API division param: fetch 'All' when multiple selected or 'All' chosen
  const apiDivision = useMemo(() => {
    if (!selectedDivisions) return null;
    if (selectedDivisions.includes('All')) return 'All';
    if (selectedDivisions.length > 1) return 'All';
    return selectedDivisions[0];
  }, [selectedDivisions]);

  // Fetch financial data when API division changes
  useEffect(() => {
    if (!apiDivision) return;
    let cancelled = false;
    if (!hasLoadedRef.current) setInitialLoading(true);
    setRefreshing(true);
    setError(null);

    async function load() {
      try {
        const [finData, aopData] = await Promise.all([
          getFinancials('2026', apiDivision),
          getAopTargets('2026', apiDivision),
        ]);
        if (!cancelled) {
          setFinancials(finData);
          setAopTargets(aopData);
          setContractRollup('All');
          setPwinOverrides({});
          hasLoadedRef.current = true;
        }
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) {
          setInitialLoading(false);
          setRefreshing(false);
        }
      }
    }
    load();
    return () => { cancelled = true; };
  }, [apiDivision]);

  // Fetch growth data for pipeline KPIs
  useEffect(() => {
    let cancelled = false;
    getGrowthDashboard()
      .then((result) => { if (!cancelled) setGrowthData(result); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // Close dropdowns on outside click
  useEffect(() => {
    function handleClick(e) {
      if (programDropdownRef.current && !programDropdownRef.current.contains(e.target)) setProgramDropdownOpen(false);
      if (customerDropdownRef.current && !customerDropdownRef.current.contains(e.target)) setCustomerDropdownOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // Aggregate divisions data: filter by selected divisions, then merge
  const divisionData = useMemo(() => {
    if (!financials?.divisions) return null;
    const divs = financials.divisions;
    let divKeys = Object.keys(divs);
    if (divKeys.length === 0) return null;

    // Filter to only selected divisions when multi-selecting
    if (selectedDivisions && !selectedDivisions.includes('All')) {
      divKeys = divKeys.filter((dk) => selectedDivisions.includes(dk));
      if (divKeys.length === 0) return null;
    }

    const merged = {
      by_deal_type: {},
      programs: [],
      totals: {},
    };

    divKeys.forEach((dk) => {
      const d = divs[dk];
      // Merge programs
      if (d.programs) {
        merged.programs = merged.programs.concat(d.programs);
      }
      // Merge by_deal_type
      if (d.by_deal_type) {
        DEAL_TYPES.forEach((dt) => {
          if (!d.by_deal_type[dt]) return;
          if (!merged.by_deal_type[dt]) merged.by_deal_type[dt] = {};
          MONTH_KEYS.forEach((mk) => {
            const entry = d.by_deal_type[dt][mk];
            if (!entry) return;
            if (!merged.by_deal_type[dt][mk]) {
              merged.by_deal_type[dt][mk] = { revenue: 0, is_actual: entry.is_actual };
            }
            merged.by_deal_type[dt][mk].revenue += entry.revenue || 0;
          });
        });
      }
      // Merge totals
      if (d.totals) {
        MONTH_KEYS.forEach((mk) => {
          const entry = d.totals[mk];
          if (!entry) return;
          if (!merged.totals[mk]) {
            merged.totals[mk] = { revenue: 0, is_actual: entry.is_actual };
          }
          merged.totals[mk].revenue += entry.revenue || 0;
        });
      }
    });

    return merged;
  }, [financials, selectedDivisions]);

  // Aggregate AOP targets
  const aopData = useMemo(() => {
    if (!aopTargets?.targets) return null;
    const targets = aopTargets.targets;
    let divKeys = Object.keys(targets);
    if (divKeys.length === 0) return null;

    // Filter to only selected divisions when multi-selecting
    if (selectedDivisions && !selectedDivisions.includes('All')) {
      divKeys = divKeys.filter((dk) => selectedDivisions.includes(dk));
      if (divKeys.length === 0) return null;
    }

    // Merge selected division targets into monthly totals
    const monthly = {};
    divKeys.forEach((dk) => {
      const divTarget = targets[dk];
      // AOP targets are structured as { BACKLOG: { "2026-01": 500000 }, ... }
      DEAL_TYPES.forEach((dt) => {
        if (!divTarget[dt]) return;
        MONTH_KEYS.forEach((mk) => {
          const val = divTarget[dt][mk];
          if (val !== undefined && val !== null) {
            monthly[mk] = (monthly[mk] || 0) + val;
          }
        });
      });
    });
    return monthly;
  }, [aopTargets, selectedDivisions]);

  // Check if any filter is active (beyond default)
  const hasActiveFilters = contractRollup !== 'All' || filterCustomer !== 'All' || !filterContractType.includes('All');

  // Filter programs by all active filters
  const filteredPrograms = useMemo(() => {
    if (!divisionData?.programs) return [];
    let result = divisionData.programs;
    if (contractRollup !== 'All') result = result.filter((p) => p.contract_rollup === contractRollup);
    if (filterCustomer !== 'All') result = result.filter((p) => p.customer === filterCustomer);
    if (!filterContractType.includes('All')) result = result.filter((p) => filterContractType.includes(p.contract_type));
    return result;
  }, [divisionData, contractRollup, filterCustomer, filterContractType]);

  // Get contract rollup options
  const rollupOptions = useMemo(() => {
    if (!divisionData?.programs) return ['All'];
    const set = new Set();
    divisionData.programs.forEach((p) => {
      if (p.contract_rollup) set.add(p.contract_rollup);
    });
    return ['All', ...Array.from(set).sort()];
  }, [divisionData]);

  // Build unique filter options from program data
  const filterOptions = useMemo(() => {
    if (!divisionData?.programs) return { customers: [], contractTypes: [] };
    const customers = new Set();
    const contractTypes = new Set();
    divisionData.programs.forEach((p) => {
      if (p.customer) customers.add(p.customer);
      if (p.contract_type) contractTypes.add(p.contract_type);
    });
    return {
      customers: Array.from(customers).sort(),
      contractTypes: Array.from(contractTypes).sort(),
    };
  }, [divisionData]);

  // Reset additional filters when division changes
  useEffect(() => {
    setFilterCustomer('All');
    setFilterContractType(['All']);
  }, [selectedDivisions]);

  // Get New Business deals for pWin panel
  const newBizDeals = useMemo(() => {
    if (!filteredPrograms) return [];
    const deals = filteredPrograms
      .filter((p) => p.deal_type === 'NEW_BIZ' && p.nb_pwin !== undefined && p.nb_pwin !== null && p.nb_pwin < 1)
      .map((p) => ({
        name: p.contract_name,
        programId: p.program_id,
        pwin: p.nb_pwin,
        months: p.months || {},
      }));
    if (pwinSort === 'pwin') {
      deals.sort((a, b) => pwinSortDir === 'asc' ? a.pwin - b.pwin : b.pwin - a.pwin);
    } else {
      deals.sort((a, b) => pwinSortDir === 'asc' ? a.name.localeCompare(b.name) : b.name.localeCompare(a.name));
    }
    return deals;
  }, [filteredPrograms, pwinSort, pwinSortDir]);

  // Compute monthly revenue by deal type, applying contract rollup filter and pWin overrides
  const chartData = useMemo(() => {
    if (!divisionData) return null;

    const byDealType = {};
    DEAL_TYPES.forEach((dt) => { byDealType[dt] = new Array(12).fill(0); });
    let lastActualIdx = -1;

    if (!hasActiveFilters && Object.keys(pwinOverrides).length === 0) {
      // Use aggregated by_deal_type data directly (faster path)
      DEAL_TYPES.forEach((dt) => {
        const dtData = divisionData.by_deal_type[dt];
        if (!dtData) return;
        MONTH_KEYS.forEach((mk, i) => {
          const entry = dtData[mk];
          if (entry) {
            byDealType[dt][i] = entry.revenue || 0;
            if (entry.is_actual && i > lastActualIdx) lastActualIdx = i;
          }
        });
      });
    } else {
      // Need program-level data for filtering and pWin adjustments
      filteredPrograms.forEach((prog) => {
        const dt = prog.deal_type;
        if (!byDealType[dt]) return;
        MONTH_KEYS.forEach((mk, i) => {
          const entry = prog.months?.[mk];
          if (!entry) return;
          let rev = entry.revenue || 0;

          // Revenue in DynamoDB is already pWin-discounted.
          // Default (unchecked): use stored value as-is.
          // Override (checked): divide by nb_pwin to show full undiscounted revenue.
          if (dt === 'NEW_BIZ' && prog.nb_pwin !== undefined && prog.nb_pwin !== null && prog.nb_pwin > 0 && prog.nb_pwin < 1) {
            if (pwinOverrides[prog.contract_name]) {
              rev = rev / prog.nb_pwin;
            }
          }

          byDealType[dt][i] += rev;
          if (entry.is_actual && i > lastActualIdx) lastActualIdx = i;
        });
      });

      // If no programs matched, check totals for actual flag
      if (lastActualIdx === -1 && divisionData.totals) {
        MONTH_KEYS.forEach((mk, i) => {
          if (divisionData.totals[mk]?.is_actual && i > lastActualIdx) lastActualIdx = i;
        });
      }
    }

    // Monthly totals
    const monthlyTotals = new Array(12).fill(0);
    DEAL_TYPES.forEach((dt) => {
      byDealType[dt].forEach((val, i) => { monthlyTotals[i] += val; });
    });

    return { byDealType, monthlyTotals, lastActualIdx };
  }, [divisionData, filteredPrograms, hasActiveFilters, pwinOverrides]);

  // Compute KPI values
  const kpis = useMemo(() => {
    if (!chartData || !aopData) {
      return {
        totalForecast: 0, gapToAop: 0, pctOfPlan: 0,
        backlog: 0, backlogPct: 0,
        recompete: 0, recompetePct: 0,
        ytdActuals: 0, ytdPctVariance: 0, actualMonths: 0,
      };
    }

    const totalForecast = chartData.monthlyTotals.reduce((a, b) => a + b, 0);
    const totalAop = MONTH_KEYS.reduce((sum, mk) => sum + (aopData[mk] || 0), 0);
    const gapToAop = totalForecast - totalAop;
    const pctOfPlan = totalAop > 0 ? ((totalForecast - totalAop) / totalAop) * 100 : 0;

    const backlog = chartData.byDealType.BACKLOG.reduce((a, b) => a + b, 0);
    const recompete = chartData.byDealType.RECOMPETE.reduce((a, b) => a + b, 0);
    const backlogPct = totalForecast > 0 ? Math.round((backlog / totalForecast) * 100) : 0;
    const recompetePct = totalForecast > 0 ? Math.round((recompete / totalForecast) * 100) : 0;

    let ytdActuals = 0;
    let ytdAop = 0;
    let actualMonths = 0;
    chartData.monthlyTotals.forEach((val, i) => {
      if (i <= chartData.lastActualIdx) {
        ytdActuals += val;
        ytdAop += aopData[MONTH_KEYS[i]] || 0;
        actualMonths++;
      }
    });
    const ytdVariance = ytdActuals - ytdAop;
    const ytdPctVariance = ytdAop > 0 ? ((ytdActuals - ytdAop) / ytdAop) * 100 : 0;

    return { totalForecast, gapToAop, pctOfPlan, backlog, backlogPct, recompete, recompetePct, ytdActuals, ytdVariance, ytdPctVariance, actualMonths };
  }, [chartData, aopData]);

  // Variance data
  const varianceData = useMemo(() => {
    if (!chartData || !aopData) return null;

    const monthly = [];
    const monthlyPct = [];
    MONTH_KEYS.forEach((mk, i) => {
      const forecast = chartData.monthlyTotals[i];
      const target = aopData[mk] || 0;
      const diff = forecast - target;
      monthly.push(Math.abs(diff) < 0.01 ? 0 : Math.round(diff * 100) / 100);
      monthlyPct.push(target > 0 ? ((forecast - target) / target) * 100 : 0);
    });

    const cumulative = [];
    const cumulativePct = [];
    let cumSum = 0;
    let cumForecast = 0;
    let cumTarget = 0;
    monthly.forEach((v, i) => {
      cumSum += v;
      cumulative.push(Math.abs(cumSum) < 0.01 ? 0 : Math.round(cumSum * 100) / 100);
      cumForecast += chartData.monthlyTotals[i];
      cumTarget += aopData[MONTH_KEYS[i]] || 0;
      cumulativePct.push(cumTarget > 0 ? ((cumForecast - cumTarget) / cumTarget) * 100 : 0);
    });

    return { monthly, monthlyPct, cumulative, cumulativePct };
  }, [chartData, aopData]);

  // Chart font sizes based on presentation mode
  const chartFontSm = presentationMode ? 13 : 10;
  const chartFontMd = presentationMode ? 14 : 11;
  const chartLabelFont = presentationMode ? `600 14px ${FONT_MONO}` : `600 11px ${FONT_MONO}`;

  // Revenue Forecast Chart
  useEffect(() => {
    if (!revenueChartRef.current || !chartData) return;
    if (revenueChartInstance.current) revenueChartInstance.current.destroy();

    const colors = chartColors(theme);
    const { byDealType, lastActualIdx } = chartData;

    const makeDataset = (dealType, fillTarget) => {
      const data = byDealType[dealType];
      const color = LAYER_COLORS[dealType];
      return {
        label: DEAL_TYPE_LABELS[dealType],
        data,
        fill: fillTarget,
        backgroundColor: data.map((_, i) => i <= lastActualIdx ? color.solid : color.forecast),
        borderColor: data.map((_, i) => i <= lastActualIdx ? color.border : color.forecastBorder),
        borderWidth: 1.5,
        pointRadius: 0,
        pointHoverRadius: 4,
        tension: 0,
        segment: {
          backgroundColor: (ctx) =>
            ctx.p0DataIndex > lastActualIdx ? color.forecast : color.solid,
          borderColor: (ctx) =>
            ctx.p0DataIndex > lastActualIdx ? color.forecastBorder : color.border,
          borderDash: (ctx) =>
            ctx.p0DataIndex > lastActualIdx ? [4, 3] : undefined,
        },
      };
    };

    const ctx = revenueChartRef.current.getContext('2d');
    revenueChartInstance.current = new Chart(ctx, {
      type: 'line',
      data: {
        labels: MONTHS,
        datasets: [
          makeDataset('BACKLOG', 'origin'),
          makeDataset('RECOMPETE', '-1'),
          makeDataset('NB_OG', '-1'),
          makeDataset('NEW_BIZ', '-1'),
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        layout: { padding: { top: 20 } },
        interaction: { mode: 'index', intersect: false },
        plugins: {
          legend: {
            display: true,
            position: 'bottom',
            labels: {
              color: colors.textMuted,
              font: { family: FONT_MONO, size: chartFontMd },
              boxWidth: presentationMode ? 14 : 12,
              padding: presentationMode ? 20 : 16,
              usePointStyle: true,
            },
          },
          tooltip: {
            mode: 'index',
            intersect: false,
            titleFont: { family: FONT_MONO, size: chartFontMd },
            bodyFont: { family: FONT_MONO, size: chartFontMd },
            footerFont: { family: FONT_MONO, size: chartFontMd },
            callbacks: {
              label: (ctx) => ` ${ctx.dataset.label}: ${formatCurrency(ctx.raw)}`,
              footer: (items) => {
                const total = items.reduce((sum, item) => sum + item.raw, 0);
                return `Total: ${formatCurrency(total)}`;
              },
            },
          },
        },
        scales: {
          x: {
            grid: { color: colors.gridLine },
            ticks: { color: colors.textMuted, font: { family: FONT_MONO, size: chartFontMd } },
          },
          y: {
            stacked: true,
            beginAtZero: true,
            grid: { color: colors.gridLine },
            ticks: {
              color: colors.textDim,
              font: { family: FONT_MONO, size: chartFontSm },
              callback: (value) => {
                if (value >= 1000000) return '$' + (value / 1000000).toFixed(1) + 'M';
                if (value >= 1000) return '$' + (value / 1000).toFixed(0) + 'K';
                return '$' + value;
              },
            },
          },
        },
      },
      plugins: [{
        id: 'actualForecastDivider',
        beforeDraw: (chart) => {
          if (lastActualIdx < 0 || lastActualIdx >= 11) return;
          const xScale = chart.scales.x;
          const yScale = chart.scales.y;
          const xPos = xScale.getPixelForValue(lastActualIdx + 1);
          const drawCtx = chart.ctx;
          drawCtx.save();

          // Shade the forecast region with a gray overlay
          const isDark = theme !== 'light';
          drawCtx.fillStyle = isDark ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.03)';
          drawCtx.fillRect(xPos, yScale.top, xScale.right - xPos, yScale.bottom - yScale.top);

          // Draw the divider line
          drawCtx.beginPath();
          drawCtx.setLineDash([6, 4]);
          drawCtx.strokeStyle = isDark ? 'rgba(255, 255, 255, 0.4)' : 'rgba(0, 0, 0, 0.3)';
          drawCtx.lineWidth = 1.5;
          drawCtx.moveTo(xPos, yScale.top);
          drawCtx.lineTo(xPos, yScale.bottom);
          drawCtx.stroke();

          // Labels on each side — actuals in yellow, forecast in gray
          const labelY = yScale.top - 8;
          drawCtx.font = chartLabelFont;
          drawCtx.textAlign = 'right';
          drawCtx.fillStyle = isDark ? 'rgba(214, 226, 0, 0.85)' : 'rgba(140, 148, 0, 0.8)';
          drawCtx.fillText('ACTUALS', xPos - 8, labelY);
          drawCtx.textAlign = 'left';
          drawCtx.fillStyle = isDark ? 'rgba(255, 255, 255, 0.4)' : 'rgba(0, 0, 0, 0.35)';
          drawCtx.fillText('FORECAST', xPos + 8, labelY);

          drawCtx.restore();
        },
      }],
    });

    return () => {
      if (revenueChartInstance.current) revenueChartInstance.current.destroy();
    };
  }, [chartData, theme, presentationMode, chartFontSm, chartFontMd, chartLabelFont]);

  // Variance to Plan Chart
  useEffect(() => {
    if (!varianceChartRef.current || !varianceData) return;
    if (varianceChartInstance.current) varianceChartInstance.current.destroy();

    const colors = chartColors(theme);
    const barColors = varianceData.monthly.map((v) =>
      v >= 0 ? 'rgba(52, 211, 153, 0.8)' : 'rgba(248, 113, 113, 0.8)'
    );
    const barBorderColors = varianceData.monthly.map((v) =>
      v >= 0 ? '#34d399' : '#f87171'
    );

    const ctx = varianceChartRef.current.getContext('2d');
    varianceChartInstance.current = new Chart(ctx, {
      type: 'bar',
      data: {
        labels: MONTHS,
        datasets: [
          {
            type: 'bar',
            label: 'Monthly Variance',
            data: varianceData.monthly,
            backgroundColor: barColors,
            borderColor: barBorderColors,
            borderWidth: 1,
            borderRadius: 4,
            order: 2,
          },
          {
            type: 'line',
            label: 'Cumulative Variance',
            data: varianceData.cumulative,
            borderColor: '#d6e200',
            backgroundColor: 'rgba(214, 226, 0, 0.1)',
            borderWidth: 2,
            pointRadius: 3,
            pointBackgroundColor: '#d6e200',
            pointBorderColor: '#d6e200',
            tension: 0.3,
            fill: false,
            order: 1,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: 'index', intersect: false },
        plugins: {
          legend: {
            display: true,
            position: 'bottom',
            labels: {
              color: colors.textMuted,
              font: { family: FONT_MONO, size: chartFontMd },
              boxWidth: presentationMode ? 14 : 12,
              padding: presentationMode ? 20 : 16,
              usePointStyle: true,
            },
          },
          tooltip: {
            mode: 'index',
            intersect: false,
            titleFont: { family: FONT_MONO, size: chartFontMd },
            bodyFont: { family: FONT_MONO, size: chartFontMd },
            callbacks: {
              label: (tooltipCtx) => {
                const val = tooltipCtx.raw;
                const sign = val >= 0 ? '+' : '';
                const pctArr = tooltipCtx.datasetIndex === 0 ? varianceData.monthlyPct : varianceData.cumulativePct;
                const pct = pctArr[tooltipCtx.dataIndex];
                const pctSign = pct >= 0 ? '+' : '';
                return ` ${tooltipCtx.dataset.label}: ${sign}${formatCurrency(val)}/${pctSign}${pct.toFixed(1)}%`;
              },
            },
          },
        },
        scales: {
          x: {
            grid: { color: colors.gridLine },
            ticks: { color: colors.textMuted, font: { family: FONT_MONO, size: chartFontMd } },
          },
          y: {
            grid: {
              color: (context) => context.tick.value === 0 ? colors.textMuted : colors.gridLine,
              lineWidth: (context) => context.tick.value === 0 ? 2 : 1,
            },
            ticks: {
              color: colors.textDim,
              font: { family: FONT_MONO, size: chartFontSm },
              callback: (value) => {
                const sign = value >= 0 ? '+' : '';
                if (Math.abs(value) >= 1000000) return sign + '$' + (value / 1000000).toFixed(1) + 'M';
                if (Math.abs(value) >= 1000) return sign + '$' + (value / 1000).toFixed(0) + 'K';
                return sign + '$' + value;
              },
            },
          },
        },
      },
    });

    return () => {
      if (varianceChartInstance.current) varianceChartInstance.current.destroy();
    };
  }, [varianceData, theme, presentationMode, chartFontSm, chartFontMd]);

  const handleDivisionClick = useCallback((div) => {
    if (div === 'All') {
      if (!canSeeAll) return;
      setSelectedDivisions(['All']);
      return;
    }
    setSelectedDivisions((prev) => {
      if (!prev) return [div];
      // If currently on 'All', switch to just this division
      if (prev.includes('All')) return [div];
      if (prev.includes(div)) {
        // Deselect — but don't allow empty selection
        const next = prev.filter((d) => d !== div);
        return next.length === 0 ? [div] : next;
      }
      return [...prev, div];
    });
  }, [canSeeAll]);

  return (
    <div className={presentationMode ? 'presentation-mode' : ''}>
      {/* KPI Tiles Row */}
      <div className="forecast-kpi-row">
        <div className="stat-card">
          <div className="stat-card-header">
            <span className="stat-card-label">YTD Actuals</span>
            <div className="stat-card-icon accent">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12" /></svg>
            </div>
          </div>
          <div className="stat-card-value">{formatCurrency(kpis.ytdActuals)}</div>
          <div className="stat-card-sub">
            <span style={{ color: kpis.ytdVariance >= 0 ? '#34d399' : '#f87171' }}>
              {kpis.ytdVariance >= 0 ? '+' : ''}{formatCompactCurrency(kpis.ytdVariance)}/{kpis.ytdPctVariance >= 0 ? '+' : ''}{kpis.ytdPctVariance.toFixed(1)}%
            </span> vs AOP
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-card-header">
            <span className="stat-card-label">Total Forecast</span>
            <div className="stat-card-icon accent">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="1" x2="12" y2="23" /><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" /></svg>
            </div>
          </div>
          <div className="stat-card-value">{formatCurrency(kpis.totalForecast)}</div>
          <div className="stat-card-sub">
            <span style={{ color: kpis.gapToAop >= 0 ? '#34d399' : '#f87171' }}>
              {kpis.gapToAop >= 0 ? '+' : ''}{formatCompactCurrency(kpis.gapToAop)}/{kpis.pctOfPlan >= 0 ? '+' : ''}{kpis.pctOfPlan.toFixed(1)}%
            </span> vs AOP
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-card-header">
            <span className="stat-card-label">Backlog Revenue</span>
            <div className="stat-card-icon blue">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="7" width="20" height="14" rx="2" ry="2" /><path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2" /></svg>
            </div>
          </div>
          <div className="stat-card-value">{formatCurrency(kpis.backlog)}</div>
          <div className="stat-card-sub">{kpis.backlogPct}% of total forecast</div>
        </div>

        <div className="stat-card">
          <div className="stat-card-header">
            <span className="stat-card-label">Recompete Revenue</span>
            <div className="stat-card-icon green">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="23 4 23 10 17 10" /><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" /></svg>
            </div>
          </div>
          <div className="stat-card-value">{formatCurrency(kpis.recompete)}</div>
          <div className="stat-card-sub">{kpis.recompetePct}% of total forecast</div>
        </div>

        <div className="stat-card">
          <div className="stat-card-header">
            <span className="stat-card-label">Qualified Pipeline</span>
            <div className="stat-card-icon blue">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 12h-4l-3 9L9 3l-3 9H2" /></svg>
            </div>
          </div>
          <div className="stat-card-value">{formatCompactCurrency(growthData?.kpi_summary?.qualified_pipeline_tcv)}</div>
          <div className="stat-card-sub">Total qualified TCV</div>
        </div>

        <div className="stat-card">
          <div className="stat-card-header">
            <span className="stat-card-label">Factored Pipeline</span>
            <div className="stat-card-icon" style={{ color: '#a78bfa' }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 20V10" /><path d="M18 20V4" /><path d="M6 20v-4" /></svg>
            </div>
          </div>
          <div className="stat-card-value">{formatCompactCurrency(growthData?.kpi_summary?.factored_pipeline_tcv)}</div>
          <div className="stat-card-sub">pWin-weighted</div>
        </div>

        <div className="stat-card">
          <div className="stat-card-header">
            <span className="stat-card-label">Submit vs Goal</span>
            <div className="stat-card-icon accent">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /></svg>
            </div>
          </div>
          <div className="stat-card-value">
            {growthData?.submit_vs_goal
              ? Math.round((growthData.submit_vs_goal.percent_complete || 0) * 100) + '%'
              : '--'}
          </div>
          <div className="stat-card-sub">
            {growthData?.submit_vs_goal
              ? `${formatCompactCurrency(growthData.submit_vs_goal.submitted_tcv)} / ${formatCompactCurrency(growthData.submit_vs_goal.goal_tcv)}`
              : 'FY2026'}
          </div>
        </div>
      </div>

      {/* Filter Bar */}
      <div className="forecast-filter-bar">
        <div className="forecast-filter-group">
          <span className="forecast-filter-label">Divisions</span>
          <div className="forecast-division-tabs">
            <button
              className={`tab-btn${selectedDivisions?.includes('All') ? ' active' : ''}`}
              onClick={() => handleDivisionClick('All')}
              disabled={!canSeeAll}
              title={!canSeeAll ? 'Requires senior-leader or investor access' : ''}
            >
              All
            </button>
            {DIVISIONS.map((div) => (
              <button
                key={div}
                className={`tab-btn${selectedDivisions?.includes(div) ? ' active' : ''}`}
                onClick={() => handleDivisionClick(div)}
              >
                {DIVISION_LABELS[div]}
              </button>
            ))}
          </div>
        </div>
        <div className="forecast-filter-group">
          <span className="forecast-filter-label">Program</span>
          <div className="custom-dropdown" ref={programDropdownRef}>
            <button
              className="custom-dropdown-trigger"
              onClick={() => setProgramDropdownOpen((o) => !o)}
              type="button"
            >
              <span>{contractRollup === 'All' ? 'All Programs' : contractRollup}</span>
              <svg width="10" height="6" viewBox="0 0 10 6" fill="none" style={{ marginLeft: 8, flexShrink: 0 }}>
                <path d="M1 1L5 5L9 1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
            {programDropdownOpen && (
              <div className="custom-dropdown-menu">
                {rollupOptions.map((opt) => (
                  <button
                    key={opt}
                    className={`custom-dropdown-item${contractRollup === opt ? ' active' : ''}`}
                    onClick={() => { setContractRollup(opt); setProgramDropdownOpen(false); }}
                    type="button"
                  >
                    {opt === 'All' ? 'All Programs' : opt}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
        {filterOptions.contractTypes.length > 0 && (
          <div className="forecast-filter-group">
            <span className="forecast-filter-label">Contract Type</span>
            <div className="forecast-division-tabs">
              <button
                className={`tab-btn${filterContractType.includes('All') ? ' active' : ''}`}
                onClick={() => setFilterContractType(['All'])}
              >
                All
              </button>
              {filterOptions.contractTypes.map((ct) => (
                <button
                  key={ct}
                  className={`tab-btn${filterContractType.includes(ct) ? ' active' : ''}`}
                  onClick={() => {
                    setFilterContractType((prev) => {
                      if (prev.includes('All')) return [ct];
                      if (prev.includes(ct)) {
                        const next = prev.filter((v) => v !== ct);
                        return next.length === 0 ? [ct] : next;
                      }
                      return [...prev, ct];
                    });
                  }}
                >
                  {ct}
                </button>
              ))}
            </div>
          </div>
        )}
        {filterOptions.customers.length > 0 && (
          <div className="forecast-filter-group">
            <span className="forecast-filter-label">Customer</span>
            <div className="custom-dropdown" ref={customerDropdownRef}>
              <button
                className="custom-dropdown-trigger"
                onClick={() => setCustomerDropdownOpen((o) => !o)}
                type="button"
              >
                <span>{filterCustomer === 'All' ? 'All Customers' : filterCustomer}</span>
                <svg width="10" height="6" viewBox="0 0 10 6" fill="none" style={{ marginLeft: 8, flexShrink: 0 }}>
                  <path d="M1 1L5 5L9 1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
              {customerDropdownOpen && (
                <div className="custom-dropdown-menu">
                  <button
                    className={`custom-dropdown-item${filterCustomer === 'All' ? ' active' : ''}`}
                    onClick={() => { setFilterCustomer('All'); setCustomerDropdownOpen(false); }}
                    type="button"
                  >
                    All Customers
                  </button>
                  {filterOptions.customers.map((c) => (
                    <button
                      key={c}
                      className={`custom-dropdown-item${filterCustomer === c ? ' active' : ''}`}
                      onClick={() => { setFilterCustomer(c); setCustomerDropdownOpen(false); }}
                      type="button"
                    >
                      {c}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '12px' }}>
          {refreshing && !initialLoading && (
            <span style={{
              fontSize: '11px',
              color: 'var(--bcore-text-dim)',
              fontFamily: FONT_MONO_STACK,
              textTransform: 'uppercase',
              letterSpacing: '1px',
            }}>
              Updating...
            </span>
          )}
          <button
            className={`presentation-toggle${presentationMode ? ' active' : ''}`}
            onClick={() => setPresentationMode((p) => !p)}
            title={presentationMode ? 'Exit presentation mode' : 'Presentation mode — larger text for screen sharing'}
            type="button"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
              <line x1="8" y1="21" x2="16" y2="21" />
              <line x1="12" y1="17" x2="12" y2="21" />
            </svg>
            <span>Present</span>
          </button>
        </div>
      </div>

      {/* Loading State — initial load only */}
      {initialLoading && (
        <div className="state-loading">Loading financial data...</div>
      )}

      {/* Error State */}
      {error && !initialLoading && (
        <div className="state-error">Unable to load financial data: {error}</div>
      )}

      {/* Charts */}
      {!initialLoading && (
        <>
          {/* Revenue Forecast Chart */}
          <div className="forecast-chart-section" key={`revenue-${theme}`} style={{ position: 'relative' }}>
            {refreshing && <div className="state-overlay" />}
            <div className="card">
              <div className="card-header">
                <span className="card-title">Revenue Forecast</span>
              </div>
              <div className="forecast-chart-layout">
                <div className="forecast-chart-main">
                  <div className="chart-container-lg">
                    {chartData ? (
                      <canvas ref={revenueChartRef}></canvas>
                    ) : (
                      <div className="state-empty">No financial data available</div>
                    )}
                  </div>
                </div>
                {newBizDeals.length > 0 && (
                  <div className="forecast-pwin-panel">
                    <div className="forecast-pwin-header">
                      <div className="forecast-pwin-title">New Business Scenarios</div>
                    </div>
                    <div className="forecast-pwin-colheaders">
                      <input
                        type="checkbox"
                        className="forecast-pwin-selectall"
                        checked={newBizDeals.length > 0 && newBizDeals.every((d) => pwinOverrides[d.name])}
                        onChange={(e) => {
                          const next = {};
                          if (e.target.checked) {
                            newBizDeals.forEach((d) => { next[d.name] = true; });
                          }
                          setPwinOverrides(next);
                        }}
                      />
                      <button
                        className={`forecast-pwin-colbtn${pwinSort === 'name' ? ' active' : ''}`}
                        onClick={() => {
                          if (pwinSort === 'name') { setPwinSortDir((d) => d === 'asc' ? 'desc' : 'asc'); }
                          else { setPwinSort('name'); setPwinSortDir('asc'); }
                        }}
                      >
                        Opportunity
                        {pwinSort === 'name' && (
                          <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" style={{ transform: pwinSortDir === 'asc' ? 'none' : 'rotate(180deg)', transition: 'transform 0.15s ease' }}>
                            <polyline points="6 9 12 15 18 9" />
                          </svg>
                        )}
                      </button>
                      <button
                        className={`forecast-pwin-colbtn pwin-col${pwinSort === 'pwin' ? ' active' : ''}`}
                        onClick={() => {
                          if (pwinSort === 'pwin') { setPwinSortDir((d) => d === 'asc' ? 'desc' : 'asc'); }
                          else { setPwinSort('pwin'); setPwinSortDir('asc'); }
                        }}
                      >
                        pWin
                        {pwinSort === 'pwin' && (
                          <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" style={{ transform: pwinSortDir === 'asc' ? 'none' : 'rotate(180deg)', transition: 'transform 0.15s ease' }}>
                            <polyline points="6 9 12 15 18 9" />
                          </svg>
                        )}
                      </button>
                    </div>
                    {newBizDeals.map((deal) => (
                      <label key={deal.programId || deal.name} className="forecast-pwin-item">
                        <input
                          type="checkbox"
                          checked={!!pwinOverrides[deal.name]}
                          onChange={(e) =>
                            setPwinOverrides((prev) => ({
                              ...prev,
                              [deal.name]: e.target.checked,
                            }))
                          }
                        />
                        <span className="forecast-pwin-label">{deal.name}</span>
                        <span className="forecast-pwin-value">
                          {deal.pwin >= 1 ? Math.round(deal.pwin) : Math.round(deal.pwin * 100)}%
                        </span>
                      </label>
                    ))}
                  </div>
                )}
              </div>

              {/* Collapsible Revenue Table */}
              <button
                className={`forecast-expand-bar${revenueTableOpen ? ' expanded' : ''}`}
                onClick={() => setRevenueTableOpen(!revenueTableOpen)}
              >
                <svg className="forecast-expand-chevron" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="9 18 15 12 9 6" />
                </svg>
                View Data ($000s)
              </button>
              {revenueTableOpen && chartData && (
                <div className="forecast-data-table">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Deal Type</th>
                        {MONTHS.map((m, i) => (
                          <th key={m} style={i <= chartData.lastActualIdx ? { color: '#d6e200' } : undefined}>{m}</th>
                        ))}
                        <th>Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {DEAL_TYPES.map((dt) => (
                        <tr key={dt}>
                          <td style={{ fontWeight: 600 }}>{DEAL_TYPE_LABELS[dt]}</td>
                          {chartData.byDealType[dt].map((val, i) => (
                            <td key={i} style={{ color: val < 0 ? '#f87171' : (i <= chartData.lastActualIdx ? '#d6e200' : undefined) }}>
                              {formatCompactCurrency(val)}
                            </td>
                          ))}
                          <td style={{ fontWeight: 600, color: chartData.byDealType[dt].reduce((a, b) => a + b, 0) < 0 ? '#f87171' : undefined }}>
                            {formatCompactCurrency(chartData.byDealType[dt].reduce((a, b) => a + b, 0))}
                          </td>
                        </tr>
                      ))}
                      <tr className="forecast-total-row">
                        <td>Total</td>
                        {chartData.monthlyTotals.map((val, i) => (
                          <td key={i} style={{ color: val < 0 ? '#f87171' : (i <= chartData.lastActualIdx ? '#d6e200' : undefined) }}>
                            {formatCompactCurrency(val)}
                          </td>
                        ))}
                        <td style={{ color: chartData.monthlyTotals.reduce((a, b) => a + b, 0) < 0 ? '#f87171' : undefined }}>
                          {formatCompactCurrency(chartData.monthlyTotals.reduce((a, b) => a + b, 0))}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>

          {/* Variance to Plan Chart */}
          <div className="forecast-chart-section" key={`variance-${theme}`} style={{ position: 'relative' }}>
            {refreshing && <div className="state-overlay" />}
            <div className="card">
              <div className="card-header">
                <span className="card-title">Variance to Plan</span>
                {hasActiveFilters && (
                  <span style={{ fontSize: '11px', color: 'var(--bcore-text-dim)', fontFamily: FONT_MONO_STACK, marginLeft: '12px' }}>
                    (AOP targets are set at division level — clear filters for variance)
                  </span>
                )}
              </div>
              {hasActiveFilters ? (
                <div className="state-empty" style={{ height: '200px' }}>
                  Clear filters to view variance to plan
                </div>
              ) : (
                <>
                <div className="chart-container-lg">
                  {varianceData ? (
                    <canvas ref={varianceChartRef}></canvas>
                  ) : (
                    <div className="state-empty">No variance data available</div>
                  )}
                </div>

                {/* Collapsible Variance Table */}
                <button
                  className={`forecast-expand-bar${varianceTableOpen ? ' expanded' : ''}`}
                  onClick={() => setVarianceTableOpen(!varianceTableOpen)}
                >
                  <svg className="forecast-expand-chevron" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="9 18 15 12 9 6" />
                  </svg>
                  View Data ($000s)
                </button>
                {varianceTableOpen && varianceData && chartData && (
                  <div className="forecast-data-table">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Metric</th>
                          {MONTHS.map((m, i) => (
                            <th key={m} style={i <= chartData.lastActualIdx ? { color: '#d6e200' } : undefined}>{m}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td style={{ fontWeight: 600 }}>Forecast</td>
                          {chartData.monthlyTotals.map((val, i) => (
                            <td key={i} style={{ color: val < 0 ? '#f87171' : (i <= chartData.lastActualIdx ? '#d6e200' : undefined) }}>
                              {formatCompactCurrency(val)}
                            </td>
                          ))}
                        </tr>
                        <tr>
                          <td style={{ fontWeight: 600 }}>AOP Target</td>
                          {MONTH_KEYS.map((mk, i) => {
                            const aopVal = aopData?.[mk] || 0;
                            return (
                              <td key={i} style={{ color: aopVal < 0 ? '#f87171' : (i <= chartData.lastActualIdx ? '#d6e200' : undefined) }}>
                                {formatCompactCurrency(aopVal)}
                              </td>
                            );
                          })}
                        </tr>
                        <tr className="forecast-total-row">
                          <td>Variance</td>
                          {varianceData.monthly.map((val, i) => (
                            <td key={i} className={val >= 0 ? 'variance-positive' : 'variance-negative'}>
                              {val >= 0 ? '+' : ''}{formatCompactCurrency(val)}
                            </td>
                          ))}
                        </tr>
                        <tr>
                          <td style={{ fontWeight: 600 }}>Cumulative</td>
                          {varianceData.cumulative.map((val, i) => (
                            <td key={i} className={val >= 0 ? 'variance-positive' : 'variance-negative'}>
                              {val >= 0 ? '+' : ''}{formatCompactCurrency(val)}
                            </td>
                          ))}
                        </tr>
                      </tbody>
                    </table>
                  </div>
                )}
                </>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

ForecastDashboard.propTypes = {
  onNavigate: PropTypes.func.isRequired,
  user: PropTypes.shape({
    email: PropTypes.string,
    groups: PropTypes.arrayOf(PropTypes.string),
  }),
};

export default ForecastDashboard;
